import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table } from "semantic-ui-react";
import { Pie, Column } from "@ant-design/plots";
import jsPDF from "jspdf";
import { Inspection, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite, useCompany, useLanguage } from "../../../../contexts";
import { LoadingSpinner, ExportPdfButton, KPICard, YearBar, ChartLegend } from "../../../Shared";
import { renderDonutForPdf, renderColumnForPdf, buildStandardDashboardPdf, captureElementAtWidth, prepareKpiCardsForPdf, drawPill, SEMANTIC_HEX } from "../../../Shared/pdfCharts";
import { ChlCardIcon } from "../ChlCardIcon";
import "./ChecklistDashboard.scss";

const inspectionCtrl = new Inspection();
const siteCtrl        = new Site();
// Verde para la barra "Inspecciones por mes" — mismo GREEN_PALETTE que
// usan los demás dashboards.
const GREEN_PALETTE = ["#7B9355", "#C4D693", "#A3B76B"];
// Misma gama que las donas de los demás dashboards.
const DONUT_PALETTE = ["#C4D693", "#A3B76B", "#8CCAFF", "#001936"];
const MONTH_BAR_Y_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFontSize: 8,
  labelLineHeight: 8,
  labelFill: "#001936",
  labelOpacity: 1,
  gridStroke: "#D3D3D3",
  gridStrokeOpacity: 1,
  gridLineWidth: 1,
  gridLineDash: [5, 6],
};
const MONTH_BAR_X_AXIS = { labelFill: "#001936", labelOpacity: 1 };
const MONTH_BAR_INTERACTION = { tooltip: { crosshairs: false }, elementHighlightByX: false };
const CURRENT_YEAR  = new Date().getFullYear();
const YEAR_OPTIONS  = Array.from({ length: 6 }, (_, i) => {
  const y = CURRENT_YEAR - 2 + i;
  return { key: y, value: y, text: String(y) };
});
const MONTH_KEYS = ["month_jan", "month_feb", "month_mar", "month_apr", "month_may", "month_jun", "month_jul", "month_aug", "month_sep", "month_oct", "month_nov", "month_dec"];

const INSP_STATUS_COLOR = { draft: "grey", completed: "green", with_actions: "blue", closed: "teal", cancelled: "red" };
const INSP_STATUS_KEY   = { draft: "draft", completed: "insp_completed", with_actions: "insp_with_actions", closed: "closed", cancelled: "insp_cancelled" };

function semaphoreVariant(pct) {
  if (pct >= 80) return "green";
  if (pct >= 60) return "orange";
  return "red";
}

function formatDate(d) {
  if (!d) return "-";
  const str = typeof d === "string" ? d : new Date(d).toISOString();
  const [y, m, day] = str.split("T")[0].split("-");
  return `${day}/${m}/${y}`;
}

function EmptyMsg({ text = "Sin datos" }) {
  return <div className="cldash__empty">{text}</div>;
}

const FilterDropdown = ({ options, value, onChange, placeholder, clearable = false }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // El menú abre siempre hacia abajo — si el filtro queda cerca del borde
  // inferior de la pantalla, no entra y se corta contra el viewport. Si no
  // hay lugar debajo pero sí arriba, abre para arriba en su lugar.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuMaxHeight = 340;
      setOpenUp(window.innerHeight - rect.bottom < menuMaxHeight && rect.top > menuMaxHeight);
    }
    setOpen((v) => !v);
  };

  const selected = options.find((o) => String(o.value) === String(value));
  const hasValue = clearable && Boolean(value);

  return (
    <div className="cldash-fd" ref={ref}>
      <button type="button" className="cldash-fd-toggle" onClick={toggleOpen}>
        <span className={selected ? "" : "cldash-fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        {hasValue && (
          <span
            role="button"
            aria-label="clear"
            className="cldash-fd-clear"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
          >
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5"/>
            </svg>
          </span>
        )}
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`cldash-fd-arrow${open ? " cldash-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className={`cldash-fd-menu${openUp ? " cldash-fd-menu--up" : ""}`}>
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`cldash-fd-item${String(o.value) === String(value) ? " cldash-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export function ChecklistDashboard({ year: yearProp, reload, companyName, siteName }) {
  const { accessToken }      = useAuth();
  const { site: contextSite } = useSite();
  const { company } = useCompany();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const kpiRef    = useRef();
  const tableRef  = useRef();
  const [exportingPdf, setExportingPdf] = useState(false);
  const exportingRef = useRef(false);

  const [internalYear, setInternalYear] = useState(yearProp || CURRENT_YEAR);
  const [items,        setItems]        = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [companySiteIds, setCompanySiteIds] = useState([]);

  const noSiteInCtx    = !contextSite || contextSite === "0" || contextSite === 0;
  const noCompanyInCtx = !company     || company     === "0" || company     === 0;

  // sync year prop → internal
  useEffect(() => {
    if (yearProp) setInternalYear(yearProp);
  }, [yearProp]);

  // Sitios de la empresa activa (para no traer datos de otras empresas del
  // mismo tenant cuando no hay un sitio puntual elegido).
  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, company, true)
      .then((res) => setCompanySiteIds((res || []).map((s) => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [accessToken, company, noCompanyInCtx]);

  useEffect(() => {
    setItems(null);
    const params = { year: internalYear };
    if (!noSiteInCtx) {
      params.site = contextSite;
    } else if (!noCompanyInCtx && companySiteIds.length > 0) {
      params.site = companySiteIds.join(",");
    }
    inspectionCtrl.getInspections(accessToken, params)
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch(() => setItems([]));
  }, [accessToken, internalYear, contextSite, reload, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    if (!items) return {};
    const total          = items.length;
    const conCumpl       = items.filter((i) => i.cumplimiento !== null && i.cumplimiento !== undefined);
    const avgCumpl       = conCumpl.length
      ? Math.round((conCumpl.reduce((s, i) => s + i.cumplimiento, 0) / conCumpl.length) * 10) / 10
      : 0;
    const hallazgos      = items.reduce((s, i) => s + (i.hallazgos || []).filter((h) => h.status === "open").length, 0);
    const conAcciones    = items.filter((i) => i.status === "with_actions").length;
    return { total, avgCumpl, hallazgos, conAcciones };
  }, [items]);

  // ── Chart data ─────────────────────────────────────────────────────────────
  const categoryData = useMemo(() => {
    if (!items) return [];
    const counts = {};
    items.forEach((i) => {
      const cat = i.template?.category || t("no_category");
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [items]);

  const monthData = useMemo(() => {
    if (!items) return [];
    const counts = Array(12).fill(0);
    items.forEach((i) => {
      if (i.date) counts[new Date(i.date).getMonth()]++;
    });
    return MONTH_KEYS.map((key, idx) => ({ mes: t(key), value: counts[idx] }));
  }, [items]);

  const statusData = useMemo(() => {
    if (!items) return [];
    const counts = {};
    items.forEach((i) => {
      const key = i.status ? t(INSP_STATUS_KEY[i.status] || i.status) : t("no_status");
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [items]);

  // ── Filtered table ─────────────────────────────────────────────────────────
  const displayItems = useMemo(() => {
    if (!items) return [];
    return items.filter((i) => {
      if (filterStatus && i.status !== filterStatus) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const tpl  = (i.template?.name || "").toLowerCase();
        const site = (i.site?.name || "").toLowerCase();
        const insp = (i.signature?.name || [i.inspector?.firstname, i.inspector?.lastname].filter(Boolean).join(" ")).toLowerCase();
        if (!tpl.includes(q) && !site.includes(q) && !insp.includes(q)) return false;
      }
      return true;
    });
  }, [items, filterSearch, filterStatus]);

  // ── Chart configs ──────────────────────────────────────────────────────────
  const pieConfig = (data) => {
    const colors = data.map((_, i) => DONUT_PALETTE[i % DONUT_PALETTE.length]);
    const total  = data.reduce((s, d) => s + d.value, 0);
    return {
      data,
      angleField: "value",
      colorField: "type",
      innerRadius: 0.6,
      scale: { color: { range: colors } },
      label: false,
      legend: false,
      tooltip: {
        title: "type",
        items: [{
          field: "value",
          name: t("quantity"),
          valueFormatter: (v) => `${v} (${total > 0 ? ((v / total) * 100).toFixed(1) : "0.0"}%)`,
        }],
      },
      height: 250,
    };
  };

  const colMonthConfig = {
    data: monthData,
    xField: "mes",
    yField: "value",
    colorField: "mes",
    scale: { color: { range: GREEN_PALETTE } },
    legend: false,
    height: 250,
    // Asset Figma "Rectangle 28.svg": 25×127, rx5 — barra angosta con las
    // 4 esquinas redondeadas.
    style: { radius: 5, maxWidth: 25, shadowColor: "transparent", shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0 },
    axis: { x: MONTH_BAR_X_AXIS, y: MONTH_BAR_Y_AXIS },
    tooltip: { items: [{ channel: "y", name: t("chk_inspections_done") }] },
    interaction: MONTH_BAR_INTERACTION,
  };

  const hasMonthData = monthData.some((d) => d.value > 0);

  const STATUS_OPTIONS = Object.keys(INSP_STATUS_KEY).map((k) => ({ key: k, value: k, text: t(INSP_STATUS_KEY[k]) }));

  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportingPdf(true);
    const chartCleanups = [];
    try {
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const categoryColorMap = Object.fromEntries(categoryData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));
      const statusColorMap = Object.fromEntries(statusData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));

      const [categoryDonut, monthColumn, statusDonut] = await Promise.all([
        renderDonutForPdf(categoryData, categoryColorMap, { showPercentLabels: true }),
        renderColumnForPdf(monthData, { xField: "mes", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25 }),
        renderDonutForPdf(statusData, statusColorMap, { showPercentLabels: true }),
      ]);
      [categoryDonut, monthColumn, statusDonut].forEach((r) => { if (r.cleanup) chartCleanups.push(r.cleanup); });

      const kpiCanvas = await captureElementAtWidth(kpiRef.current, 1400, {}, prepareKpiCardsForPdf);

      const tableColumns = [
        { key: "checklist", header: t("checklist"), flex: 1.8, value: (r) => r.template?.name || "-" },
        { key: "site", header: t("site"), flex: 1.2, value: (r) => r.site?.name || "-" },
        {
          key: "inspector", header: t("chk_inspector"), flex: 1.4,
          value: (r) => r.signature?.name || [r.inspector?.firstname, r.inspector?.lastname].filter(Boolean).join(" ") || "-",
        },
        { key: "date", header: t("date"), flex: 1, value: (r) => formatDate(r.date) },
        {
          key: "compliance", header: t("chk_compliance"), flex: 1, align: "center",
          render: (doc, { x, y, width, height, row }) => {
            const hasValue = row.cumplimiento !== null && row.cumplimiento !== undefined;
            const text = hasValue ? `${row.cumplimiento.toFixed(1)}%` : "N/D";
            const color = hasValue ? SEMANTIC_HEX[semaphoreVariant(row.cumplimiento)] : SEMANTIC_HEX.grey;
            drawPill(doc, { x: x + width / 2, y: y + height / 2, text, color });
          },
        },
        {
          key: "status", header: t("status"), flex: 1, align: "center",
          render: (doc, { x, y, width, height, row }) => {
            if (!row.status) return;
            drawPill(doc, {
              x: x + width / 2, y: y + height / 2,
              text: t(INSP_STATUS_KEY[row.status] || row.status),
              color: SEMANTIC_HEX[INSP_STATUS_COLOR[row.status]] || SEMANTIC_HEX.grey,
            });
          },
        },
      ];

      await buildStandardDashboardPdf({
        jsPDFCtor: jsPDF,
        pageTitle: `${t("checklist")} — ${internalYear}`,
        companyName: [companyName, siteName].filter(Boolean).join(" · "),
        generatedOnLabel: t("generated_on"),
        kpiCanvas,
        chartRows: [
          {
            height: 210,
            cards: [
              { title: t("chk_by_category"), ...categoryDonut, legendPosition: "right" },
              { title: t("chk_inspections_by_month"), ...monthColumn },
              { title: t("chk_by_status"), ...statusDonut, legendPosition: "right" },
            ],
          },
        ],
        table: { columns: tableColumns, rows: displayItems, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
        tableTitle: t("checklist"),
        filename: `checklist_${internalYear}.pdf`,
      });
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      chartCleanups.forEach((fn) => fn());
      exportingRef.current = false;
      setExportingPdf(false);
    }
  };

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="cldash">
      {/* KPI header */}
      <div className="cldash__kpi-header">
        <YearBar year={internalYear} options={YEAR_OPTIONS} onChange={setInternalYear} />
        <ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} title={t("export_pdf")} />
      </div>
      <div className="cldash__kpis" ref={kpiRef}>
        <KPICard
          label={t("chk_inspections_done")}
          value={kpis.total}
        />
        <KPICard
          label={t("chk_avg_compliance")}
          value={`${kpis.avgCumpl}%`}
          rawValue={kpis.avgCumpl}
          showProgress
          progressColor={semaphoreVariant(kpis.avgCumpl)}
        />
        <KPICard
          label={t("chk_open_findings")}
          value={kpis.hallazgos}
          subtitle={t("chk_no_closure_action")}
        />
        <KPICard
          label={t("chk_pending_actions")}
          value={kpis.conAcciones}
          subtitle={t("chk_with_actions_status")}
        />
      </div>

      {/* Charts */}
      <div className="cldash__charts">
        <div className="cldash__chart-box">
          <div className="cldash__chart-title-bar">
            <div className="cldash__chart-title"><ChlCardIcon />{t("chk_by_category")}</div>
          </div>
          <div className="cldash__chart-content">
            {categoryData.length ? (
              <div className="cldash__pie-row">
                <div className="chartWrapper cldash__pie-wrapper">
                  <Pie {...pieConfig(categoryData)} />
                </div>
                <ChartLegend items={categoryData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
              </div>
            ) : (
              <div className="chartWrapper"><EmptyMsg text={t("no_data")} /></div>
            )}
          </div>
        </div>

        <div className="cldash__chart-box">
          <div className="cldash__chart-title-bar">
            <div className="cldash__chart-title"><ChlCardIcon />{t("chk_inspections_by_month")}</div>
          </div>
          <div className="cldash__chart-content">
            <div className="chartWrapper">
              {hasMonthData ? <Column {...colMonthConfig} /> : <EmptyMsg text={t("no_data")} />}
            </div>
          </div>
        </div>

        <div className="cldash__chart-box">
          <div className="cldash__chart-title-bar">
            <div className="cldash__chart-title"><ChlCardIcon />{t("chk_by_status")}</div>
          </div>
          <div className="cldash__chart-content">
            {statusData.length ? (
              <div className="cldash__pie-row">
                <div className="chartWrapper cldash__pie-wrapper">
                  <Pie {...pieConfig(statusData)} />
                </div>
                <ChartLegend items={statusData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
              </div>
            ) : (
              <div className="chartWrapper"><EmptyMsg text={t("no_data")} /></div>
            )}
          </div>
        </div>
      </div>

      {/* Filtros + tabla, en una sola card */}
      <div className="cldash__card" ref={tableRef}>
        <div className="cldash__filters">
          <div className="cldash__search-wrapper">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              className="cldash__search-input"
              placeholder={t("chk_search_placeholder")}
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
            />
          </div>
          <div className="cldash__filter-select">
            <FilterDropdown options={STATUS_OPTIONS} value={filterStatus} onChange={setFilterStatus} placeholder={t("status")} clearable />
          </div>
        </div>

        {/* Table */}
        <div className="cldash__tbl-scroll">
          <Table className="cldash__table unstackable rtable-cards">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t("checklist")}</Table.HeaderCell>
                <Table.HeaderCell>{t("site")}</Table.HeaderCell>
                <Table.HeaderCell>{t("chk_inspector")}</Table.HeaderCell>
                <Table.HeaderCell>{t("date")}</Table.HeaderCell>
                <Table.HeaderCell>{t("chk_compliance")}</Table.HeaderCell>
                <Table.HeaderCell>{t("status")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {displayItems.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6} textAlign="center" style={{ color: "#999", padding: "1.5rem" }}>
                    {t("no_results")}
                  </Table.Cell>
                </Table.Row>
              ) : displayItems.map((i) => {
                const stColor = INSP_STATUS_COLOR[i.status] || "grey";
                const stLabel = i.status ? t(INSP_STATUS_KEY[i.status] || i.status) : "-";
                const inspector = i.signature?.name || [i.inspector?.firstname, i.inspector?.lastname].filter(Boolean).join(" ") || "-";
                return (
                  <Table.Row key={i._id}>
                    <Table.Cell data-label={t("checklist")} style={{ textAlign: "left" }}>{i.template?.name || "-"}</Table.Cell>
                    <Table.Cell data-label={t("site")}>{i.site?.name || "-"}</Table.Cell>
                    <Table.Cell data-label={t("chk_inspector")}>{inspector}</Table.Cell>
                    <Table.Cell data-label={t("date")}>{formatDate(i.date)}</Table.Cell>
                    <Table.Cell data-label={t("chk_compliance")} textAlign="center">
                      {i.cumplimiento !== null && i.cumplimiento !== undefined ? (
                        <span className="cldash__row-pill" style={{ backgroundColor: SEMANTIC_HEX[semaphoreVariant(i.cumplimiento)], color: "#fff" }}>
                          {i.cumplimiento.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="cldash__row-pill" style={{ backgroundColor: SEMANTIC_HEX.grey, color: "#fff" }}>N/D</span>
                      )}
                    </Table.Cell>
                    <Table.Cell data-label={t("status")} textAlign="center">
                      <span className="cldash__row-pill" style={{ backgroundColor: SEMANTIC_HEX[stColor] || SEMANTIC_HEX.grey, color: "#fff" }}>{stLabel}</span>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      </div>
    </div>
  );
}
